"use client";

import React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { User, Star } from '@/components/icons';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { Link } from 'react-router-dom';
import { ProductChatItem as ProductChatItemType } from '@/lib/supabase';

interface ProductChatItemProps {
  chat: ProductChatItemType;
}

const ProductChatItem: React.FC<ProductChatItemProps> = ({ chat }) => {
  const displayName = chat.profiles?.display_name || null;
  const username = chat.profiles?.username || 'Usuario Desconocido';
  const avatarUrl = chat.profiles?.avatar_url;

  // Render star rating if it exists
  const renderStars = (rating: number | undefined | null) => {
    if (!rating) return null;
    return (
      <div className="flex gap-0.5 my-1 items-center">
        {[1, 2, 3, 4, 5].map((starIndex) => {
          const isFilled = rating >= starIndex;
          const isHalf = rating >= starIndex - 0.5 && rating < starIndex;

          return (
            <div key={starIndex} className="relative h-4 w-4">
              {/* Background empty star */}
              <Star className="h-4 w-4 text-muted-foreground absolute inset-0" />
              {/* Filled overlay - half or full */}
              {(isFilled || isHalf) && (
                <div
                  className="absolute inset-0 overflow-hidden"
                  style={{
                    width: isFilled ? '100%' : '50%',
                  }}
                >
                  <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                </div>
              )}
            </div>
          );
        })}
        <span className="text-xs text-muted-foreground ml-1">{rating} / 5</span>
      </div>
    );
  };

  return (
    <div className="flex gap-3 p-2 rounded-md bg-muted/30">
      <div className="flex-shrink-0">
        <Link to={`/${username}`}>
          <Avatar className="h-8 w-8">
            <AvatarImage src={avatarUrl || undefined} alt={displayName || username} />
            <AvatarFallback>
              {(displayName ? displayName.charAt(0) : username.charAt(0)).toUpperCase()}
            </AvatarFallback>
          </Avatar>
        </Link>
      </div>
      <div className="flex-1">
        <div className="flex flex-col gap-0.5">
          <div>
            <Link to={`/${username}`} className="font-semibold text-sm hover:underline">
              {displayName || `@${username}`}
            </Link>
          </div>
          <div>
            <span className="text-xs text-muted-foreground">
              {formatDistanceToNow(new Date(chat.created_at), { addSuffix: true, locale: es })}
            </span>
          </div>
        </div>
        {renderStars(chat.rating)}
        <p className="text-sm text-foreground mt-1">{chat.message}</p>
      </div>
    </div>
  );
};

export default ProductChatItem;